import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import gql from 'graphql-tag';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {isInaccessibleInfo} from 'auth/RequestAccess';
import {ProcessTaskStatusType, ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getBadgeStatus} from 'notebook/reviews/ReviewBadge';
import {ReviewBadgeStatus} from 'notebook/reviews/ReviewConstants';
import {getStatusIcon} from 'notebook/reviews/ReviewUtil';
import {WorksheetViewActionType, WorksheetViewStateType} from 'notebook/worksheets/WorksheetConstants';
import {WorksheetContext} from 'notebook/worksheets/WorksheetContext';
import {
  PMEIProcedureQuery,
  PMEIProcedureQuery_procedureMethodExecutionInstance_ProcedureMethodExecutionInstanceNode_procedureRun_ProcedureRunNode as ProcedureRunNode,
  PMEIProcedureQuery_procedureMethodExecutionInstance_ProcedureMethodExecutionInstanceNode_procedureRun_ProcedureRunNode_methodExecutionInstances_ProcedureMethodExecutionInstanceNode_procedureRunConditionReplicate_ProcedureRunConditionReplicateNode as ProcedureRunConditionReplicateNode,
  PMEIProcedureQueryVariables,
} from 'procedures/worksheets/__generated/PMEIProcedureQuery';
import TaskStatusIcon from 'processes/status/TaskStatusIcon';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import WorksheetBreadcrumbs from './WorksheetBreadcrumbs';
import WorksheetConditionsLabel from './WorksheetConditionsLabel';

export const PMEI_PROCEDURE_QUERY = gql`
  query PMEIProcedureQuery($worksheetId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    procedureMethodExecutionInstance(worksheetId: $worksheetId) {
      ... on ProcedureMethodExecutionInstanceNode {
        id
        procedureRun {
          id
          name
          procedureType
          study {
            ... on StudyNode {
              id
              name
              editUrl
              studySchema {
                id
                studyType
              }
            }
            ... on InaccessibleInfoNode {
              ...InaccessibleInfoGraphQL
            }
          }
          methodExecutionInstances {
            methodTask {
              ... on ProcessTaskNode {
                id
                status {
                  id
                  statusType
                }
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
            id
            procedureRunConditionReplicate {
              id
              replicateNumber
              procedureRunCondition {
                id
                name
              }
              # this might be something we need to reconsider when we rework how we subscribe to realtime updates
              processRootTask {
                ... on ProcessTaskNode {
                  id
                  process {
                    id
                  }
                }
                ... on InaccessibleInfoNode {
                  ...InaccessibleInfoGraphQL
                }
              }
            }
            planMethodDefinitionVersion {
              ... on BaseProcedureMethodDefinitionVersionInterface {
                id
                name
                sourceMethodId
              }
            }
            templateMethodVersion {
              ... on ProcedureMethodDefinitionVersionNode {
                id
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
            worksheet {
              ... on WorksheetNode {
                id
                name
              }
              ... on ReviewableInterface {
                id
                review {
                  id
                  status
                }
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface Worksheet {
  id: string;
  worksheetName: string;
  worksheetReviewStatus?: ReviewStatus;
  templateMethodId: string;
  taskStatus: ProcessTaskStatusType;
  procedureRunConditionReplicate: ProcedureRunConditionReplicateNode;
  isInaccessible: boolean;
}

interface WorksheetNameWithStatusProps {
  name: string;
  taskStatus: ProcessTaskStatusType;
  reviewStatus?: ReviewStatus;
}

export function WorksheetNameWithStatusIcon({
  name,
  taskStatus,
  reviewStatus,
}: WorksheetNameWithStatusProps): JSX.Element {
  if (reviewStatus) {
    const badgeStatus = getBadgeStatus(reviewStatus);
    const style = STYLE_FOR_REVIEW_STATUS[badgeStatus];
    const icon = getStatusIcon(reviewStatus);
    return (
      <>
        <BenchlingIcon className={classnames(css(styles.reviewStatusIcon, style))} icon={icon} />
        {name}
      </>
    );
  } else {
    return (
      <>
        <TaskStatusIcon type={taskStatus} />
        {name}
      </>
    );
  }
}

export interface WorksheetMetadataComponentProps {
  worksheetId: string;
  worksheetName: string;
}

export default function WorksheetMetadataComponent({
  worksheetId,
  worksheetName,
}: WorksheetMetadataComponentProps): JSX.Element {
  const initialWorksheetId = useRef<string>(worksheetId);
  const [cachedProcedureRun, setCachedProcedureRun] = useState<ProcedureRunNode>();
  const {dispatch, worksheetViewState, isExecutionEnabled} = WorksheetContext.useContext();

  // Since we're only trying to get to the ProcedureRun from the Worksheet. The ProcedureRun will be the same
  // no matter what `worksheetId` you pass in for the run. So we'll always use initial one passed in.
  const procedureResult = useBenchlingQuery<PMEIProcedureQuery, PMEIProcedureQueryVariables>(
    PMEI_PROCEDURE_QUERY,
    {
      variables: {worksheetId: initialWorksheetId.current},
    }
  );
  const refetch = useCallback(async (): Promise<void> => {
    await procedureResult.refetch();
  }, [procedureResult]);

  useEffect(() => {
    if (
      procedureResult.type === QueryResultType.SUCCESS &&
      procedureResult.data.procedureMethodExecutionInstance &&
      !isInaccessibleInfo(procedureResult.data.procedureMethodExecutionInstance) &&
      cachedProcedureRun !== procedureResult.data.procedureMethodExecutionInstance.procedureRun &&
      worksheetViewState === WorksheetViewStateType.LOADED
    ) {
      // If the worksheet is loaded and the procedure is loaded and it is different from our previously cached
      // procedure run, we can set the new procedure run in the cache and mark the worksheet as PROCEDURE_LOADED.
      // This will, in turn, set isExecutionEnabled to true in the worksheet reducer to signal that the worksheet
      // is now fully ready.
      const procedureRun = procedureResult.data.procedureMethodExecutionInstance.procedureRun;
      dispatch({type: WorksheetViewActionType.PROCEDURE_LOADED, procedureType: procedureRun.procedureType});
      setCachedProcedureRun(procedureRun);
    } else if (
      procedureResult.type !== QueryResultType.PENDING &&
      worksheetViewState === WorksheetViewStateType.RELOADING &&
      cachedProcedureRun
    ) {
      // If we don't have a pending query in flight and the worksheet has been reloaded, we can reset the cached
      // procedure run and refetch the query.
      setCachedProcedureRun(undefined);
      refetch();
    }
  }, [procedureResult, cachedProcedureRun, dispatch, worksheetViewState, refetch, isExecutionEnabled]);

  const currentMethodExecutionInstance = cachedProcedureRun?.methodExecutionInstances.find(
    (method) => getAccessibleOrInaccessibleId(method.worksheet) === worksheetId
  );

  if (cachedProcedureRun != null && currentMethodExecutionInstance != null) {
    return (
      <div className={css(styles.container)}>
        <BenchlingIcon className={css(styles.headerIcon)} icon="document" />
        <div className={css(styles.linkAndDropdownsContainer)}>
          <WorksheetBreadcrumbs procedureRun={cachedProcedureRun} worksheetName={worksheetName} />
          <div className={css(styles.worksheetNameAndConditionsLabel)}>
            <Popover renderContent={() => <div>{worksheetName}</div>} placement={FloatingPlacement.BOTTOM}>
              <div className={css(styles.worksheetName)}>{worksheetName}</div>
            </Popover>
            {!isInaccessible(cachedProcedureRun.study) && (
              <WorksheetConditionsLabel
                worksheetId={worksheetId}
                studyType={cachedProcedureRun.study.studySchema.studyType}
                procedureType={cachedProcedureRun.procedureType}
                methodExecutionInstances={cachedProcedureRun.methodExecutionInstances}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={css(styles.container)}>
      <BenchlingIcon className={css(styles.headerIcon)} icon="document" />
      <Popover renderContent={() => <div>{worksheetName}</div>} placement={FloatingPlacement.BOTTOM}>
        <div className={css(styles.worksheetName)}>{worksheetName}</div>
      </Popover>
    </div>
  );
}

export const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  worksheetName: {
    fontSize: '18px',
    lineHeight: '20px',
    fontWeight: 600,
    maxWidth: '560px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  worksheetNameAndConditionsLabel: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: '2px',
  },
  headerIcon: {
    color: Color.GRAY_700,
    height: '32px',
    width: '24px',
    marginRight: '12px',
  },
  linkAndDropdownsContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  reviewStatusIcon: {
    marginRight: '6px',
  },
  accepted: {
    color: Color.GREEN_600,
  },
  needsReview: {
    color: Color.ORANGE_500,
  },
  inProgress: {
    color: Color.BLUE_500,
  },
  redButton: {
    color: Color.RED_500,
  },
});

export const STYLE_FOR_REVIEW_STATUS = {
  [ReviewBadgeStatus.ACCEPTED]: styles.accepted,
  [ReviewBadgeStatus.REJECTED]: styles.redButton,
  [ReviewBadgeStatus.IN_PROGRESS]: styles.inProgress,
  [ReviewBadgeStatus.IN_REVIEW]: styles.needsReview,
  [ReviewBadgeStatus.ACTION_REQUIRED]: styles.redButton,
};
